import {
  faUser,
  // faCartShopping,
  // faBullhorn,
  faMessageQuestion,
  faArrowRightFromBracket,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Link } from '@/components/ui/link/link.component';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { formatCustomerPreferredName } from '@/utils/formatters/format-customer-preferred-name';
import { formatUserInitials } from '@/utils/formatters/format-user-initials';
import { authStore } from '@/store/auth.store';
import { useLogout } from '@/hooks/use-logout.hook';
import { phonePatientSac } from '@/utils/consts/sac.consts';
import { useWhatsappLinkWithAnalytics } from '@/hooks/use-whatsapp/use-whatsapp-link-with-analytics.hook';
import { FEATURE_MENU, type MenuFeatureName } from '@/feature-flags/consts/feature-menu.const';
import { useEnabledFeatures } from '@/feature-flags/hooks/use-feature-flag.hook';
import { logDebug } from '@/feature-flags/utils/logger';
import { getCurrentEvaluationId } from '@/feature-flags/utils/correlation';

interface ProfileMenuItem {
  label: string;
  url: string;
  icon: IconDefinition;
  isNew: boolean;
  testId: string;
  target?: string;
  rel?: string;
  analyticsEvent?: string;
}

const MENU_FEATURES: MenuFeatureName[] = [
  'newAppointments',
  'newProfile',
  'newDependents',
  'newFavorites',
];

export function ProfileSheet() {
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const logout = useLogout();
  const whatsappSacLink = useWhatsappLinkWithAnalytics(phonePatientSac, 'Olá! Sou paciente Medprev e preciso de suporte.');
  const whatsappSacUrl = whatsappSacLink.url;
  const { enabled: enabledFeatures } = useEnabledFeatures(MENU_FEATURES);

  const accountItems = useMemo(() => {
    const hiddenFeatures = MENU_FEATURES.filter((feature) => !enabledFeatures.includes(feature));

    logDebug('Menu items filtered', {
      feature_flag: {
        event: 'menu_items_rendered',
        evaluation_id: getCurrentEvaluationId(),
        total_features: MENU_FEATURES.length,
        visible_features: enabledFeatures,
        hidden_features: hiddenFeatures,
        source: 'ProfileSheet',
      },
    });

    return enabledFeatures.map((feature): ProfileMenuItem => {
      const meta = FEATURE_MENU[feature];
      return {
        label: meta.label,
        url: meta.internalRoute,
        icon: meta.icon,
        isNew: false,
        testId: meta.testId,
      };
    });
  }, [enabledFeatures]);

  const menuItems = useMemo<ProfileMenuItem[]>(() => [
    ...accountItems,
    // { label: 'Feedback', url: '/feedback', icon: faBullhorn, isNew: false, testId: DataTestIdEnum.ITEM_FEEDBACK },
    {
      label: 'Fale com a gente',
      url: whatsappSacUrl,
      icon: faMessageQuestion,
      isNew: false,
      target: '_blank',
      rel: 'noopener noreferrer',
      testId: DataTestIdEnum.ITEM_HELP,
      analyticsEvent: AnalyticsEnum.CLICK_PROFILE_MENU_TALK_TO_US,
    },
  ], [accountItems, whatsappSacUrl]);

  const userItems = useMemo(() => {
    return {
      name: authenticatedUser ? formatCustomerPreferredName(authenticatedUser)?.split(' ')[0] : 'Visitante',
      thumbnail: null,
    };
  }, [authenticatedUser]);

  const initials = useMemo(
    () => (authenticatedUser ? formatUserInitials(formatCustomerPreferredName(authenticatedUser)) : ''),
    [authenticatedUser],
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          data-testid={DataTestIdEnum.HEADER_USER_BUTTON}
          variant="secondary"
          color="primary"
          aria-label={initials ? `Perfil de ${userItems.name}` : 'Perfil'}
          className="w-8 h-8 min-w-8 p-0 rounded-full text-sm shrink-0"
        >
          {initials || <Icon icon={faUser} className="w-4 h-4" />}
        </Button>
      </SheetTrigger>
      <SheetContent
        aria-label="Menu da conta"
        aria-labelledby={undefined}
        title="Menu da conta"
        side="right"
        hasHeader
        className="w-full sm:w-[25rem] text-gray-800 text-lg overflow-y-auto p-0"
        data-testid={DataTestIdEnum.PROFILE_MENU_CONTENT}
      >
        <SheetHeader className="flex-row items-center gap-4 space-y-0 py-4 pl-6 pr-5">
          <SheetTitle data-testid={DataTestIdEnum.PROFILE_MENU_GREETING} className="flex-1 text-2xl font-normal text-gray-900">
            <span>Olá,</span>
            {' '}
            <span className="font-bold">
              {userItems.name}
              !
            </span>
          </SheetTitle>
          <SheetClose asChild>
            <ModalCloseButton />
          </SheetClose>
        </SheetHeader>

        <ul className="flex flex-col content-center !items-start">
          {menuItems.map((item) => (
            <li
              key={item.label}
              className="flex flex-row w-full"
            >
              <SheetClose asChild>
                <Link
                  data-testid={item.testId}
                  prefetch={false}
                  href={item.url}
                  target={item.target}
                  rel={item.rel}
                  data-analytics-event={item.analyticsEvent}
                  className="flex items-center gap-2 py-4 w-full px-6 text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
                >
                  <Icon icon={item.icon} className="w-5 h-4 shrink-0" />
                  <span className="text-lg font-normal">{item.label}</span>
                  {item.isNew && (
                    <div className="bg-green-100 text-success-700 text-base font-semibold border border-success-400 self-center px-2 rounded h-fit">
                      Novo
                    </div>
                  )}
                </Link>
              </SheetClose>
            </li>
          ))}
        </ul>
        <SheetFooter>
          <SheetClose
            data-testid={DataTestIdEnum.LOGOUT}
            onClick={logout}
            className="flex items-center gap-2 py-4 w-full px-6 text-left text-gray-800 hover:bg-gray-100 focus-visible:bg-gray-50 focus-visible:text-success-700 transition-colors duration-300"
          >
            <Icon icon={faArrowRightFromBracket} className="w-5 h-4 shrink-0" />
            <span className="text-lg">Sair</span>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
